import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import back_app from '../../lib/back_app';
import BrandHeader from '../../components/brand/BrandHeader';

const ASSET_TYPES = [
  {
    value: 'video_ad',
    label: 'Video Ad',
    icon: '📺',
    description: 'Video advertisement (max 10 seconds)',
    formats: '.mp4, .mov',
    maxSize: '10MB',
    specs: '1080×1920 (9:16) recommended'
  },
  {
    value: 'banner',
    label: 'Banner Ad',
    icon: '🖼️',
    description: 'Static image banner',
    formats: '.png, .jpg',
    maxSize: '5MB',
    specs: '1920×1080 (16:9) recommended'
  },
];

export default function AssetUpload() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const return_to = searchParams.get('return_to') || '/brand/assets';
  const return_lbl = return_to.includes('/create-campaign') || return_to.includes('/campaigns/')
    ? 'Back to Campaign' : 'View Assets';
  const [loading, setLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    asset_type: 'video_ad',
    file: null as File | null,
  });
  
  const [preview, setPreview] = useState<string | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setFormData({ ...formData, file });
      
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setPreview(reader.result as string);
        };
        reader.readAsDataURL(file);
      } else if (file.type.startsWith('video/')) {
        const url = URL.createObjectURL(file);
        setPreview(url);
      } else {
        setPreview(null);
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.file) {
      alert('Please select a file');
      return;
    }

    setLoading(true);

    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', formData.file);
      formDataUpload.append('asset_type', formData.asset_type);
      formDataUpload.append('name', formData.name);

      const result = await back_app.basset_insert(formDataUpload);

      if (!result.err) {
        setShowSuccess(true);
      } else {
        alert('Error: ' + (result.err || 'Upload failed'));
      }
    } catch (error) {
      console.error('Error uploading asset:', error);
      alert('Error uploading asset');
    } finally {
      setLoading(false);
    }
  };

  const selectedType = ASSET_TYPES.find(t => t.value === formData.asset_type);
  const isVideo = formData.asset_type === 'video_ad';

  return (
    <div className="min-h-screen bg-gray-50">
      <BrandHeader />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {}
        <button
          onClick={() => navigate(return_to)}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6"
        >
          <span>←</span>
          <span>{return_lbl}</span>
        </button>

        {}
        <div className="mb-6">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">Upload Asset</h2>
          <p className="text-gray-600 mt-2">
            Upload brand assets for your campaigns
          </p>
        </div>

        {}
        <form onSubmit={handleSubmit} className="bg-white rounded-xl border shadow-sm p-4 sm:p-6 space-y-6">
          {}
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">
              Asset Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., Summer Sale Video"
              required
              className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {}
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-3">
              Asset Type <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {ASSET_TYPES.map((type) => (
                <label
                  key={type.value}
                  className={`
                    relative flex items-start p-4 border-2 rounded-xl cursor-pointer transition-all
                    ${
                      formData.asset_type === type.value
                        ? 'border-blue-500 bg-gradient-to-r from-blue-50 to-purple-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }
                  `}
                >
                  <input
                    type="radio"
                    name="asset_type"
                    value={type.value}
                    checked={formData.asset_type === type.value}
                    onChange={(e) => {
                      setFormData({ ...formData, asset_type: e.target.value });
                      setPreview(null);
                      setFormData(prev => ({ ...prev, file: null }));
                    }}
                    className="mt-1 w-4 h-4 text-blue-600"
                  />
                  <div className="ml-3 flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xl">{type.icon}</span>
                      <span className="font-bold text-gray-900">{type.label}</span>
                    </div>
                    <div className="text-sm text-gray-600 mb-1">{type.description}</div>
                    <div className="text-xs text-gray-500">
                      <div>{type.formats} • Max {type.maxSize}</div>
                      <div className="mt-0.5">{type.specs}</div>
                    </div>
                  </div>
                  {formData.asset_type === type.value && (
                    <div className="absolute top-3 right-3">
                      <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                        <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                        </svg>
                      </div>
                    </div>
                  )}
                </label>
              ))}
            </div>
          </div>

          {}
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">
              Upload File <span className="text-red-500">*</span>
            </label>
            
            <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 sm:p-8 text-center hover:border-blue-400 transition-colors">
              <input
                type="file"
                onChange={handleFileChange}
                accept={isVideo ? 'video}
                  <div className="max-w-md mx-auto">
                    {isVideo ? (
                      <video
                        src={preview}
                        controls
                        className="w-full rounded-lg border-2 border-gray-200"
                      />
                    ) : (
                      <img
                        src={preview}
                        alt="Preview"
                        className="w-full rounded-lg border-2 border-gray-200"
                      />
                    )}
                  </div>

                  {}
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-sm font-semibold text-gray-900">{formData.file?.name}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {((formData.file?.size || 0) / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>

                  {}
                  <label htmlFor="file-upload">
                    <span className="inline-block px-6 py-2 border-2 border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 cursor-pointer transition-all">
                      Change File
                    </span>
                  </label>
                </div>
              ) : (
                <label htmlFor="file-upload" className="cursor-pointer block">
                  <div className="text-4xl sm:text-5xl mb-4">
                    {isVideo ? '📺' : '🖼️'}
                  </div>
                  <p className="text-base sm:text-lg text-gray-900 font-semibold mb-2">
                    Click to upload {isVideo ? 'video' : 'image'}
                  </p>
                  <p className="text-sm text-gray-500 mb-1">
                    {selectedType?.formats}
                  </p>
                  <p className="text-xs text-gray-400">
                    Max {selectedType?.maxSize} • {selectedType?.specs}
                  </p>
                </label>
              )}
            </div>

            {}
            <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-xs text-blue-800">
                <span className="font-semibold">💡 Tip:</span> {isVideo 
                  ? 'Keep videos under 10 seconds for best performance. Use 9:16 (vertical) format for mobile viewing.'
                  : 'Use high-quality images with clear branding. 16:9 format works best for most placements.'}
              </p>
            </div>
          </div>

          {}
          <div className="flex flex-col sm:flex-row gap-3 pt-4">
            <button
              type="button"
              onClick={() => navigate(return_to)}
              className="w-full sm:flex-1 px-6 py-3 border-2 border-gray-300 rounded-lg font-semibold text-gray-700 hover:bg-gray-50 transition-all"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !formData.file}
              className="w-full sm:flex-1 px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-lg font-semibold hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Uploading...
                </span>
              ) : (
                'Upload Asset'
              )}
            </button>
          </div>
        </form>
      </div>

      {showSuccess && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-in fade-in duration-200"
          onClick={() => { setShowSuccess(false); navigate(return_to); }}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6 sm:p-8 text-center animate-in zoom-in-95 duration-200"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mx-auto w-16 h-16 rounded-full bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center mb-4 shadow-lg">
              <svg className="w-9 h-9 text-white" fill="none" stroke="currentColor" strokeWidth={3} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Asset uploaded successfully</h3>
            <p className="text-sm text-gray-600 mb-6">Your asset is ready to use in campaigns.</p>
            <button
              onClick={() => { setShowSuccess(false); navigate(return_to); }}
              className="w-full px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-lg font-semibold hover:shadow-lg transition-all"
            >
              {return_lbl}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}